import { apiIntegrations } from "@/api/integrations/integration.api";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  useIntegrationDisconnect,
  useIntegrations,
} from "@/queries/integrations/use-integrations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiIntegrationType } from "@/types/integrations/integration.types.ts";
import {
  Button,
  ConfirmationModal,
  Page,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { parseInt } from "lodash";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export const SettingsIntegrationZoom = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const disconnect = useIntegrationDisconnect();
  const { handleErrors } = useErrorHandle();

  useTitle(
    t("integrations:zoom_title"),
    t("integrations:settings_integrations_title"),
    t("common:settings")
  );

  const integrationId = id ? parseInt(id, 10) : undefined;
  const { data: integrations, isLoading } = useIntegrations();
  const integration = integrations?.find((x) => x.id === integrationId);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.integrations.manageViewed, {
      [analyticProperties.source]: "Zoom",
    });
  });

  const handleDelete = async () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("integrations:disconnect_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        if (!integration) return;
        const result = await disconnect.mutateAsync(integration.id);

        if (result?.success) {
          analyticsService.trackEvent(analyticEvents.integrations.disconnected, {
            [analyticProperties.actionType]: "Zoom",
          });
          navigate(`/settings/integrations`);
        }
      },
    });
  };

  const handleConnect = async () => {
    analyticsService.trackEvent(analyticEvents.integrations.connectStarted, {
      [analyticProperties.actionType]: "Zoom",
    });

    const { data, error } = await apiIntegrations.install(ApiIntegrationType.zoom);

    if (data) {
      window.location.href = data.authorization_url;
    } else handleErrors(error);
  };

  if (integration && !integration.is_active) {
    return (
      <Page>
        <Page.Header
          title={t("integrations:zoom_title")}
          showBack={true}
          navigateTo={"/settings/integrations"}
        />
        <Page.Content>
          <Stack gap={"xl"} className={"container-sm mx-auto"}>
            <Title
              header={t("integrations:zoom_not_connected")}
              caption={t("integrations:zoom_not_connected_description")}
              paddingBottom
              paddingTop
            />
            <Stack gap={"sm"}>
              <DemoUnavailable className="w-full">
                <Button onClick={handleConnect} size={"lg"} className={"w-full"}>
                  {t("integrations:connect")}
                </Button>
              </DemoUnavailable>
            </Stack>
          </Stack>
        </Page.Content>
      </Page>
    );
  }

  return (
    <Page>
      <Page.Header
        title={t("integrations:zoom_title")}
        showBack={true}
        navigateTo={"/settings/integrations"}
      />
      <Page.Content>
        <Stack gap={"xl"} className={"container-sm mx-auto"}>
          {isLoading && <Skeleton className={"w-full h-24"} />}
          {integration && (
            <>
              <Title
                header={t("integrations:zoom_connected")}
                caption={t("integrations:zoom_connected_description")}
                paddingBottom
                paddingTop
              />
              <DeleteListItem
                onDelete={handleDelete}
                title={t("integrations:disconnect") + " Zoom"}
                caption={integration.identity}
                deleteTitle={t("integrations:disconnect")}
              />
            </>
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
};
